import React from 'react'

import User from '../../models/User'

import ClientUsers from '../clients/ClientUsersList'
import ClientUsersPage from '../clients/ClientUsersList'

import ErrorBanner from '../../components/ErrorBanner'
import LoadingBanner from '../../components/LoadingBanner'
import {useLocation, useNavigate, useParams} from "react-router-dom";
import FormPage from "../FormPage";
import InternationPhoneInput from "../../components/form/InternationPhoneInput";

export default class EditUser extends FormPage
{
    static path = '/org/members/:userId/edit';

    /**
     * @var {Integer}
     */
    static IncludeExtraFields = true;

    /**
     * The title shown on the form
     */
	static FormTitle = "clients.edit_company_users.title";

	/**
     * The api endpoint path that the form should send a post request to
     */
	static FormPost = "/api/clients/{client_id}/users/{user_id}/update";

    /**
     * The submit button title for the form
     */
    static FormSubmit = "clients.edit_company_users.submit_button";

    /**
     * [Constructor]
     *
     * @param {Object} props
     * @returns {FormPage}
     */
    constructor(props)
    {
		super(props);

		this.state = {
			...this.state,
            isLoading: true,
            error: null,
		};
	}

    componentDidMount()
    {
        super.componentDidMount();

        if (this.context?.auth?.isClient() && !(this.context?.auth?.isClientAdmin() || this.context?.auth?.isClientConsultant())) {
            return this.props.navigate(
                ClientUsers.defaultPath()
            );
        }

        this.fetchUser();
    }

    userRoleOptions()
    {
        return this.context?.auth?.manageableUserRoles();
    }

    async fetchUser()
    {
        await this.promisedState({
            model: undefined,
            isLoading: true,
            error: null,
        });

        let user = await User.fetchForClient(this.props.userId);

        if (!user) {
            // handle error
            return this.handleErrorResponse(
                User.FetchAllForClientRequest, {
                    500: 'error_show_user',
                    404: 'error_user_not_found',
                }
            );
        }

        if (user.isClientAdmin() && !(this.context?.auth?.isClientSuperAdmin() || this.context?.auth?.isClientConsultant())) {
            return this.props.navigate(
                ClientUsers.defaultPath()
            );
        }

        let model = User.make({...user})
        if (model.phone) {
            let phoneNumber = model.phone.replace(/\D/g, '');
            if (phoneNumber?.length === 10 && !model.phone.includes('+')) {
                model.phone = "+1 " + model.phone;
            }
        }
        model.client_project_groups = TB.map(model.client_project_groups || [], group => {
            return TB.mapValues(group, (value, key) => {
                if (TB.isDefined(value) && typeof value === 'boolean') {
                    return value ? 1 : 0;
                }
                return value
            })
        })
        model = this.mapping({...model})

        await this.promisedState({model, user});

        await this.promisedState({
            isLoading: false,
        });
    }

   	/**
     * Maps form input data to form submit model
     * Appends client id to form data from auth user assigned client
     *
     * @param {Object} model
     * @returns {Object}
     */
	mapping(model)
	{
		let user = this.context.auth;

		model.client_id = user.clientOrg().id;
		model.user_id = this.props.userId;

        if ((user.isClientAdmin() || user.isClientConsultant()) && this.state.model?.client_project_groups) {
            model.client_project_groups = TB.map([...(this.state.model.client_project_groups || [])], group => {
                return TB.mapValues(group, (value, key) => {
                    if (TB.isDefined(value) && typeof value === 'boolean') {
                        return value ? 1 : 0;
                    }
                    return value
                })
            });
        }

        model.image = model.image_path;

		return model;
	}

	/**
     * Handles successful responses returned by TB Request
     *
     * @param {Object} responseData
     * @returns void
     */
	async onSuccess(responseData)
	{
        let user = User.make({...responseData.data.user});

        await User.clearCache();

        TB.alert(
			'confirm',
			'clients.edit_company_users.success.title',
			TB.tryTrans('clients.edit_company_users.success.message', {name: user.fullName()})
		);

        return this.props.navigate({
            pathname: ClientUsersPage.pathFor(user.status),
            key: TB.uuid()
        }, {replace: true});
	}

    /**
     * Render the form
     *
     * @returns {JSX.Element}
     */
    renderForm()
    {
        if (this.state.isLoading) {
            return <LoadingBanner message="clients.edit_company_users.loading"/>;
        }

        if (this.state.error) {
            return <ErrorBanner {...this.state.error}
                onRetry={this.fetchUser.bind(this)}/>
        }

        if (!this.state.model) {
            return (<div/>);
        }

        return super.renderForm();
    }

    /**
     * The css class for the page container
     *
     * @var {String}
     */
    static BGClass = 'bordered-panel client-user-form';

    /**
     * The max width in pixels for the form container
     *
     * @var {Integer}
     */
    static MaxWidth = 800;

    /**
     * @returns {Object}
     */
    navBar()
    {
        return {
            back: {
                path: ClientUsersPage.defaultPath(),
                title: TB.tryTrans('clients.create_company_user.back_link'),
            },
        };
    }

    /**
     * The fields mapping for the form
     */
    formFields()
    {
        let fields = [];

        if (this.constructor.IncludeExtraFields) {
            fields.push({
                fields: {
                    image_path: {
                        label: 'clients.create_company_user.picture_label',
                        type: 'pixie',
                        ratio: '1:1',
                        circular: false,
                        required: false,
                        maxWidth: 400
                    },
                },
            });
        }

        fields.push({
            style: styles.fieldSection,
            fields: {
                first_name: {
                    label: 'clients.create_company_user.first_name_label',
                    required: true,
                    columns: 6,
                    style: styles.field,
                },
                last_name: {
                    label: 'clients.create_company_user.last_name_label',
                    required: true,
                    columns: 6,
                    style: styles.field,
                },
            },
        });

        let emailField = {
            label: 'clients.create_company_user.email_label',
            type: 'email',
            required: true,
            columns: 6,
            style: styles.field,
        };

        let roleField = {
            label: 'clients.create_company_user.role_label',
            type: 'select',
            options: this.userRoleOptions() || [],
            required: true,
            columns: 6,
            style: styles.field,
        };

		let supplierFields = []
		if (this.context.auth?.clientOrg?.()?.is_supplier && this.context.auth?.canManageClientResourceTalent()) {
			supplierFields.push({
				// style: styles.fieldSection,
				fields: {
					supplier_title: (
						<div>
							<p>
								Access to Supplier Resource Management Portal
							</p>
							<p className="text-sm mt-1">
								Set whether this user has access to the Supplier dashboard where they can invite talent resources,
								edit the organization profile, and manage/pursue jobs for talent.
							</p>
						</div>
					),
					can_manage_resource_talent: {
						label: 'Yes, this user can manage talent resources',
						required: false,
						columns: 6,
						style: styles.field,
						type: 'checkbox'
					},
				},
			})
		}
        if (!this.constructor.IncludeExtraFields) {

            let sectionFields = {
                email: emailField,
            };

            if (this.userRoleOptions()) {
                sectionFields.role = roleField;
            }

            return fields.concat([
                {
                    style: styles.fieldSection,
                    fields: sectionFields,
                },
            ]);
        }

        fields = fields.concat([

            {
                style: styles.fieldSection,
                fields: {
                    email: emailField,
                    phone: {
                        type: 'antd-input',
                        children: (
                            <InternationPhoneInput/>
                        ),
                        label: 'clients.create_company_user.phone_label',
                        required: false,
                        columns: 6,
                        style: styles.field,
                    },
                },
            },
            {
                style: styles.fieldSection,
                fields: {
                    job_title: {
                        label: 'clients.create_company_user.job_title_label',
                        required: false,
                        columns: 6,
                        style: styles.field,
                    },
                    location: {
                        label: 'clients.create_company_user.location_label',
                        type: 'location',
                        required: false,
                        columns: 6,
                        style: styles.field,
                    },
                },
            }
        ]);

        if (this.userRoleOptions() && !this.state.user?.isClientOwner?.())
        {
            fields.push({
                style: styles.fieldSection,
                fields: {
                    role: roleField,
                },
            })
        }

        fields = fields.concat(supplierFields);

        return fields;
    }

}


const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
};

const EditClientUser = (props) => {
    const {userId} = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    return (
        <EditUser userId={userId} navigate={navigate} location={location}/>
    )
}

export {
    EditClientUser
}